import { Suspense, useMemo, useState } from 'react';
import { Box, Grid2, Paper, Typography } from '@mui/material';
import CustomAccordion from '../../../../components/ui/materialui/customAccordion';
import { FiltroConsultaOCM, InscritosOCMResultDto } from '../models/filtroConsultaOCM';
import * as yup from 'yup'
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import InputSelect from '../../../../components/inputs/yup/materialui/inputSelect';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import FilterListIcon from '@mui/icons-material/FilterList';
import { UseConsultaOCM } from '../hooks/useConsultaOCM';
import { useLocation } from 'react-router-dom';
import { RowConsultaOCM } from '../models/rowConsultaOCM';
import { useColumns } from '../utils/columnas';
import Tabla from '../../../../components/ui/materialui/tabla';
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone';
import { AxiosResponse, HttpStatusCode } from 'axios';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import { useColumnsResult } from '../utils/columnasResult';
import { ModalResultados } from '../../../../components/ui/materialui/modalResultados';
import { RowResultEnvioOCM } from '../models/rowResultEnvio';
import Breadcrumb from '../../../../components/ui/materialui/breadcrumb';
import { ListResponseDto } from '../../../../dtos/ListResponseDto';

const breadcrumbItems = [
    { label: 'Inicio', url: '/' },
    { label: 'Recaudacion masiva', url: `/recaudacionMasiva` },
    { label: 'Consulta y envío OCM', active: true },
]

export default function ConsultaOCM() {

    const location = useLocation();

    const validationSchema: yup.ObjectSchema<FiltroConsultaOCM> = yup.object().shape({
        estado: yup.string().required('Requerido')
    })
    
    const {
        register,
        formState: { errors, isDirty },
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        control,
        watch,
        trigger
    } = useForm<FiltroConsultaOCM>({ resolver: yupResolver(validationSchema) })

    const { COLUMNS } = useColumns({ control });
    const columns = useMemo(() => COLUMNS, [control]);

    const { COLUMNS_RESULT } = useColumnsResult({control});
    const columnsResults = useMemo(() => COLUMNS_RESULT, [control])

    const [estado, setEstado] = useState<string | null>(null)
    const [data, setData] = useState<RowConsultaOCM[]>([])
    const [listaEstados, setListaEstados] = useState<string[]>([])

    const [showResult, setShowResult] = useState<boolean>(false);
    const [dataResult, setDataResult] = useState<RowResultEnvioOCM[]>([])

    const {descargarExcel, getInscritos, enviarExcelOCM} = UseConsultaOCM();

    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });

    const onSubmit = async (data: FiltroConsultaOCM) => {
        const response: AxiosResponse<ListResponseDto<RowConsultaOCM>> = await getInscritos(data.estado)
        if(response.status === 200 || response.status === 201){
            setData(response.data.items)
            setListaEstados(response.data.items.map((x: any) => x.observacion.toUpperCase()))
            setEstado(data.estado)
        }
    }

    const seDeshabilitaEnviar = data.length === 0 || !estado?.includes("OK") || !listaEstados.includes("OK")

    const downloadExcel = async () => {
        const now = new Date();
        const pad = (n: number) => n.toString().padStart(2, "0");

        const formatDay = `${pad(now.getDate())}${pad(now.getMonth() + 1)}${now.getFullYear()}`;
        const formatHour = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

        const response = await descargarExcel();

        if (response.status === 200 || response.status === 201) {

            const originalName = response.data.fileName || "";
            const isExcel = originalName.toLowerCase().endsWith(".xlsx");

            const fileName = isExcel
                ? `Recurrencia_Mutuaria_${formatDay}_${formatHour}.xlsx`
                : `Recurrencia_Mutuaria_${formatDay}_${formatHour}.zip`;

            // 1. Decodificar base64
            const base64 = response.data.fileContent;
            const byteCharacters = atob(base64);
            const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
            const byteArray = new Uint8Array(byteNumbers);

            // 2. Crear Blob con el tipo correcto
            const blob = new Blob(
                [byteArray],
                { type: isExcel
                    ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    : "application/octet-stream"
                }
            );

            // 3. Crear URL temporal
            const url = URL.createObjectURL(blob);

            // 4. Crear link de descarga
            const downloadLink = document.createElement("a");
            downloadLink.href = url;
            downloadLink.download = fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();

            // 5. Limpieza
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(url);
        }
    };


    const handleSendSubmit = async () => {
        const response = await enviarExcelOCM();
        if (response.status === 200 || response.status === 201){
            const updateData: AxiosResponse<ListResponseDto<RowConsultaOCM>> = await getInscritos(estado ?? "OK")
            if (updateData.status === 200 || updateData.status === 201){
                setData(updateData.data.items)
            } 
            setDataResult(response.data.enviados)
            setShowResult(true)
        }
    }

    const showModalConfirmation = () => {
        setModalConfirmacion({
            show: true,
            title: 'Enviar OCM',
            message: `¿Estás seguro que desea enviar los registros actuales al envío OCM?`,
            metodo: () => {
                handleSendSubmit();
            },
        });
    }

    return (
        <>
            <Suspense>
                <Breadcrumb items={breadcrumbItems} />
                <ConfirmationModal
                    onHide={() =>
                        setModalConfirmacion({ ...modalConfirmacion, show: false })
                    }
                    onConfirm={modalConfirmacion.metodo}
                    show={modalConfirmacion.show}
                    message={modalConfirmacion.message}
                    title={modalConfirmacion.title}
                />
                <ModalResultados 
                    open={showResult} 
                    onClose={() => setShowResult(false)} 
                    title='Resultados integración envío OCM'
                    data={dataResult} 
                    columns={COLUMNS_RESULT}
                    columnsExcel={COLUMNS_RESULT}
                    excelFileName='RESULTADOS-INTEGRACION-OCM'
                />

                <Box sx={{ mt: 2 }}>
                    <Paper elevation={3} sx={{ p: 3 }}>
                        <CustomAccordion
                            title='Filtros'
                            expandedDefault
                        >
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Grid2 container spacing={2} sx={{ marginTop: 1 }}>
                                    <InputSelect
                                        name="estado"
                                        control={control}
                                        options={[
                                            { value: "OK", label: "Ok" },
                                            { value: "ERROR", label: "Error" },
                                            { value: "OK/ERROR", label: "Ok/Error" },
                                            { value: "INSCRITOS MOROSOS", label: "Inscritos morosos" },
                                            { value: "ENVIADO/NO RECAUDADOS", label: "Enviado/No recaudados" }
                                        ]}
                                        label="Estado"
                                        onChange={(value) => setValue("estado", value ?? "")}
                                        error={errors.estado}
                                    />
                                </Grid2>
                                <Grid2 size={4} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                    <MButton type="submit" variant="contained" color="primary" startIcon={<FilterListIcon />}>
                                        Filtrar
                                    </MButton>
                                </Grid2>
                            </form>
                        </CustomAccordion>
                        <Box sx={{ mt: 4 }}>
                            <Tabla
                                data={data}
                                columns={columns}
                            />
                        </Box>
                        <Grid2 container spacing={3} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end', marginTop: "10px" }}>
                            <MButton variant="contained" color="primary" startIcon={<SummarizeTwoToneIcon />} 
                                    disabled={seDeshabilitaEnviar}
                                    onClick={downloadExcel}>
                                Generar Excel
                            </MButton>
                            <MButton variant="contained" color="success" 
                                disabled={seDeshabilitaEnviar} 
                                onClick={showModalConfirmation}>
                                Enviar
                            </MButton>
                        </Grid2>
                    </Paper>
                </Box>
            </Suspense>
        </>
    )
}